import { useCallback, useEffect, useState } from 'react';

import { Tabs } from '@mantine/core';

import { DocxContent, MarkdownParser, PdfViewer } from '@/components';
import { ANCHOR_HIGHLIGHT_EVENT } from '@/lib/anchorHighlight';

type ReportTab = 'presentation' | 'content';

const TAB_STORAGE_KEY = 'gti.report-view.tab';

function initialTab(): ReportTab {
  try {
    // Default is Content; remember each user's last choice.
    return localStorage.getItem(TAB_STORAGE_KEY) === 'presentation' ? 'presentation' : 'content';
  } catch {
    return 'content';
  }
}

interface ReportBodyProps {
  isDocx: boolean;
  /** Required for docx reports — both viewers fetch by report id. */
  reportId?: string;
  /** Markdown source for non-docx reports. */
  markdownContent?: string | null;
  /** Print rendering: no tabs/chrome, just the document itself. */
  printMode?: boolean;
  /**
   * Admins get the Content/Presentation tabs; clients only ever see the
   * Presentation (PDF) view, rendered without a tab bar (a single tab is
   * just chrome). Falls back to the Content rendering when no PDF can be
   * produced, still without tabs.
   */
  isAdmin?: boolean;
}

/**
 * The single docx/markdown fork for report content.
 *
 * Markdown reports render through MarkdownParser as before. Docx reports get
 * two views of the same source of truth: **Presentation** (server-converted
 * PDF — page-exact fidelity) and **Content** (in-browser docx rendering).
 * The Presentation tab self-hides when the server cannot produce a PDF.
 */
export function ReportBody({
  isDocx,
  reportId,
  markdownContent,
  printMode = false,
  isAdmin = false,
}: Readonly<ReportBodyProps>) {
  const [tab, setTab] = useState<ReportTab>(initialTab);
  const [pdfUnavailable, setPdfUnavailable] = useState(false);

  // A feedback-anchor highlight only exists in the Content view — flip to
  // it when the AI QA panel asks. Deliberately NOT persisted as the
  // user's tab preference (it's a navigation, not a choice).
  useEffect(() => {
    const flipToContent = () => setTab('content');
    window.addEventListener(ANCHOR_HIGHLIGHT_EVENT, flipToContent);
    return () => window.removeEventListener(ANCHOR_HIGHLIGHT_EVENT, flipToContent);
  }, []);

  const handleTabChange = (value: string | null) => {
    if (value === 'presentation' || value === 'content') {
      setTab(value);
      try {
        localStorage.setItem(TAB_STORAGE_KEY, value);
      } catch {
        // Storage unavailable (private mode) — the choice just isn't remembered.
      }
    }
  };

  const handlePdfUnavailable = useCallback(() => setPdfUnavailable(true), []);

  if (!isDocx) {
    return <MarkdownParser content={markdownContent ?? ''} />;
  }
  if (!reportId) {
    return null;
  }
  if (printMode) {
    // Print route keeps the plain docx rendering.
    return <DocxContent reportId={reportId} />;
  }

  if (!isAdmin) {
    // Clients: presentation view only, no tab chrome. If the PDF can't
    // be produced, fall back to the in-browser docx rendering.
    return pdfUnavailable ? (
      <DocxContent reportId={reportId} />
    ) : (
      <PdfViewer reportId={reportId} onUnavailable={handlePdfUnavailable} />
    );
  }

  const activeTab: ReportTab = pdfUnavailable ? 'content' : tab;

  return (
    <Tabs value={activeTab} onChange={handleTabChange} keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="content">Content</Tabs.Tab>
        {!pdfUnavailable && <Tabs.Tab value="presentation">Presentation</Tabs.Tab>}
      </Tabs.List>
      <Tabs.Panel value="content" pt="md">
        <DocxContent reportId={reportId} />
      </Tabs.Panel>
      {!pdfUnavailable && (
        <Tabs.Panel value="presentation" pt="md">
          <PdfViewer reportId={reportId} onUnavailable={handlePdfUnavailable} />
        </Tabs.Panel>
      )}
    </Tabs>
  );
}
